import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import * as api from "api/api";
import {
	OffMarketListingResource,
	OffMarketListingListResource,
	UpsertOffMarketListingResource,
} from "api/resource/off-market-listing";
import { arrayToById, objectToById } from "redux/normalize";
import { AgentControl, isAgentControl } from "type/agent-control";
import { loginReset } from "./authentication";

export interface OffMarketListingsState {
	loading?: boolean;
	error?: string | null;
	loaded: boolean;
	agentControl: AgentControl;
	automatedFeaturedSolds: boolean;
	featureAgentOffice: boolean;
	soldsOnFeaturedYn: boolean;
	byId: {
		[id: number]: OffMarketListingResource;
	};
}

const initialState: OffMarketListingsState = {
	loaded: false,
	agentControl: "none",
	automatedFeaturedSolds: false,
	featureAgentOffice: false,
	soldsOnFeaturedYn: false,
	byId: {},
};

const fetchOffMarketListings = createAsyncThunk(
	"offMarketListings/fetch",
	async (payload, thunkAPI) => {
		thunkAPI.dispatch(offMarketListingsLoading());
		const result = api.getOffMarketListings();
		result
			.then((response) => thunkAPI.dispatch(offMarketListingsLoaded(response)))
			.catch((error) => thunkAPI.dispatch(offMarketListingsFailed(error)));
		return result;
	}
);

const createOffMarketListing = createAsyncThunk(
	"offMarketListings/create",
	async (payload: { body: UpsertOffMarketListingResource }, thunkAPI) => {
		const result = api.createOffMarketListing(payload.body);
		result
			.then((listing) => thunkAPI.dispatch(offMarketListingLoaded(listing)))
			.catch((error) => thunkAPI.dispatch(offMarketListingsFailed(error)));
		return result;
	}
);

const updateOffMarketListing = createAsyncThunk(
	"offMarketListings/update",
	async (payload: { id: number; body: UpsertOffMarketListingResource }, thunkAPI) => {
		const result = api.updateOffMarketListing(payload.id, payload.body);
		result
			.then((listing) => thunkAPI.dispatch(offMarketListingLoaded(listing)))
			.catch((error) => thunkAPI.dispatch(offMarketListingsFailed(error)));
		return result;
	}
);

const deleteOffMarketListing = createAsyncThunk(
	"offMarketListings/delete",
	async (payload: { id: number }, thunkAPI) => {
		thunkAPI.dispatch(offMarketListingDeleted(payload.id));
		const result = api.deleteOffMarketListing(payload.id);
		result.catch((error) => thunkAPI.dispatch(offMarketListingsFailed(error)));
		return result;
	}
);

export const offMarketListingsSlice = createSlice({
	name: "offMarketListings",
	initialState,
	reducers: {
		offMarketListingsLoading(state) {
			state.loading = true;
		},
		offMarketListingsLoaded(state, action: PayloadAction<OffMarketListingListResource>) {
			state.loading = false;
			state.error = null;
			state.loaded = true;
			state.automatedFeaturedSolds = action.payload.automatedFeaturedSolds;
			// MAX-4194: guarded by slice/agent-control-fallback.test.ts; CI skips tests — run it
			// locally before touching this. The fallback covers exactly one deploy window: a bundle carrying this code
			// running against a WAR that predates `agentControl`. ihf-root deploys first, so once
			// it lands the field is always present and this branch is dead — delete it, together
			// with `officeAccount` on both resource interfaces, in the follow-up ticket.
			//
			// Membership test rather than `??`: `??` fires only on null/undefined, so an
			// unrecognised string from a newer server would blank the control instead.
			//
			// Note the off-market legacy pair is featureAgentOffice/officeAccount, where the
			// supplemental page spells the first one showAgentPageColumn. Same predicate, two
			// names — which is part of why the derivation moved server-side.
			state.agentControl = isAgentControl(action.payload.agentControl)
				? action.payload.agentControl
				: action.payload.featureAgentOffice && action.payload.officeAccount
					? "dropdown"
					: action.payload.officeAccount
						? "freeText"
						: "none";
			state.featureAgentOffice = action.payload.featureAgentOffice;
			state.soldsOnFeaturedYn = action.payload.soldsOnFeaturedYn;
			state.byId = arrayToById(action.payload.listings, "id");
		},
		offMarketListingLoaded(state, action: PayloadAction<OffMarketListingResource>) {
			state.loading = false;
			state.error = null;
			state.byId = {
				...state.byId,
				...objectToById(action.payload, "id"),
			};
		},
		offMarketListingDeleted(state, action: PayloadAction<number>) {
			state.loading = false;
			state.error = null;
			const byId = { ...state.byId };
			delete byId[action.payload];
			state.byId = byId;
		},
		offMarketListingsFailed(state, action: PayloadAction<any>) {
			state.loading = false;
			state.error = action.payload && action.payload.message;
		},
	},
	extraReducers(builder) {
		builder.addCase(loginReset, (state) => {
			return {
				...initialState,
				error: state.error,
			};
		});
	},
});

export const {
	offMarketListingsLoading,
	offMarketListingsLoaded,
	offMarketListingLoaded,
	offMarketListingDeleted,
	offMarketListingsFailed,
} = offMarketListingsSlice.actions;

export {
	fetchOffMarketListings,
	createOffMarketListing,
	updateOffMarketListing,
	deleteOffMarketListing,
};
